import type { PhotoItem } from '../photo-sources/types';

/**
 * The editorial layout engine (PLAN.md §6.1–6.2).
 *
 * Photos are grouped into date chapters, then flowed through magazine spread templates. The
 * whole thing is pure: given items and a container width it returns fully-positioned spreads
 * with computed heights and no DOM measurement. That is what makes windowing possible — a
 * spread's offset is known before it is ever rendered.
 *
 * Every dimension derives from the container width, so the same engine produces a four-across
 * editorial spread on a desktop and a single column on a phone.
 */

/**
 * `row` is a justified run of photos. The count is chosen per row to land near a target height
 * rather than being fixed, because a fixed count breaks down as the container changes: two
 * portraits across 1800px compute to a 1200px-tall row, while four land near 600px — and on a
 * phone anything above one is unreadably small.
 */
export type TemplateId = 'hero' | 'row' | 'offset-single' | 'stack';

export type DateOrder = 'oldest' | 'newest';
export type Grouping = 'auto' | 'day' | 'month';

export interface LayoutOptions {
  order: DateOrder;
  grouping: Grouping;
}

export const DEFAULT_LAYOUT_OPTIONS: LayoutOptions = { order: 'oldest', grouping: 'auto' };

/** `auto` groups by day for anything inside this span — a trip — and by month beyond it. */
const AUTO_DAY_SPAN_DAYS = 45;

/** A hero is not allowed within this many spreads of the last one… */
const HERO_MIN_GAP = 3;
/** …and is forced if none has appeared in this many, so no chapter reads flat. */
const HERO_MAX_GAP = 6;

/**
 * A single photo must occupy at least this fraction of the container to be worth a spread of
 * its own. Below it the height cap has shrunk the photo so far that the surrounding space reads
 * as a mistake rather than as deliberate whitespace.
 */
const MIN_SINGLE_FRACTION = 0.34;
/** Above this, the photo is substantial enough that an off-centre placement looks deliberate. */
const OFFSET_SINGLE_FRACTION = 0.55;

export interface PlacedPhoto {
  item: PhotoItem;
  /** Index into the flat ordered list — what the cinematic view opens. */
  index: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Spread {
  key: string;
  template: TemplateId;
  photos: PlacedPhoto[];
  height: number;
  /** Absolute offset from the top of the gallery. */
  top: number;
}

export interface Chapter {
  key: string;
  label: string;
  count: number;
  top: number;
  headerHeight: number;
  spreads: Spread[];
  height: number;
}

export interface Layout {
  chapters: Chapter[];
  totalHeight: number;
  /** Flat, in render order — index alignment for the cinematic view. */
  ordered: PhotoItem[];
}

// --- responsive metrics -----------------------------------------------------

interface Metrics {
  gap: number;
  rowMin: number;
  rowMax: number;
  rowTarget: number;
  rowCap: number;
  heroCap: number;
  singleCap: number;
  stackCap: number;
  /** Multi-photo templates need width to breathe; below that they just make everything tiny. */
  allowStack: boolean;
  allowSingle: boolean;
  headerHeight: number;
  chapterGap: number;
}

/**
 * Everything the layout needs, derived from the container.
 *
 * The phone case is not a scaled-down desktop: at 375px a row of two landscapes computes to
 * about 120px tall, which is a contact sheet rather than a gallery. Narrow containers get one
 * photo per row and a much higher height ceiling, since a full-width portrait is *supposed* to
 * be taller than the viewport is wide.
 */
function metricsFor(width: number, viewportHeight: number): Metrics {
  const narrow = width < 620;
  const medium = width < 1000;

  if (narrow) {
    return {
      gap: 8,
      rowMin: 1,
      rowMax: 1,
      rowTarget: viewportHeight * 0.55,
      rowCap: viewportHeight * 0.92,
      heroCap: viewportHeight * 0.5,
      singleCap: viewportHeight * 0.6,
      stackCap: viewportHeight * 0.6,
      allowStack: false,
      allowSingle: false,
      headerHeight: 84,
      chapterGap: 36,
    };
  }

  return {
    gap: medium ? 10 : 14,
    rowMin: 2,
    rowMax: medium ? 2 : width < 1400 ? 3 : 4,
    rowTarget: viewportHeight * (medium ? 0.34 : 0.4),
    rowCap: viewportHeight * 0.52,
    heroCap: viewportHeight * 0.62,
    singleCap: viewportHeight * 0.5,
    stackCap: viewportHeight * 0.5,
    allowStack: !medium,
    allowSingle: true,
    headerHeight: medium ? 96 : 118,
    chapterGap: medium ? 44 : 56,
  };
}

const aspectOf = (item: PhotoItem) => item.width / Math.max(item.height, 1);

// --- chapters ---------------------------------------------------------------

function chapterKey(iso: string | undefined, byDay: boolean): string {
  if (!iso) return 'undated';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'undated';
  const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  return byDay ? `${month}-${String(date.getDate()).padStart(2, '0')}` : month;
}

function chapterLabel(key: string, narrow: boolean): string {
  if (key === 'undated') return 'UNDATED';
  const [year, month, day] = key.split('-').map(Number);
  const date = new Date(year, month - 1, day ?? 1);
  const formatted = day
    ? date.toLocaleDateString(undefined, {
        // The weekday is the first thing to go when there's no room for it.
        weekday: narrow ? undefined : 'long',
        day: 'numeric',
        month: narrow ? 'short' : 'long',
        year: 'numeric',
      })
    : date.toLocaleDateString(undefined, { month: narrow ? 'short' : 'long', year: 'numeric' });
  return formatted.toUpperCase();
}

/** Days between the earliest and latest dated photo. Drives `auto` grouping. */
function spanInDays(dated: PhotoItem[]): number {
  if (dated.length === 0) return 0;
  let min = Infinity;
  let max = -Infinity;
  for (const item of dated) {
    const t = new Date(item.createdAt as string).getTime();
    if (t < min) min = t;
    if (t > max) max = t;
  }
  return (max - min) / 86_400_000;
}

/**
 * Sorted by capture date (Q11). Photos with no date collect into one trailing chapter rather
 * than being scattered through dated ones.
 */
function groupIntoChapters(
  items: PhotoItem[],
  options: LayoutOptions
): Array<{ key: string; items: PhotoItem[] }> {
  const dated = items.filter((i) => i.createdAt);
  const undated = items.filter((i) => !i.createdAt);

  const byDay =
    options.grouping === 'day' ||
    (options.grouping === 'auto' && spanInDays(dated) <= AUTO_DAY_SPAN_DAYS);

  const direction = options.order === 'oldest' ? 1 : -1;
  dated.sort(
    (a, b) =>
      direction *
      (new Date(a.createdAt as string).getTime() - new Date(b.createdAt as string).getTime())
  );

  const groups: Array<{ key: string; items: PhotoItem[] }> = [];
  for (const item of dated) {
    const key = chapterKey(item.createdAt, byDay);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(item);
    else groups.push({ key, items: [item] });
  }
  if (undated.length > 0) groups.push({ key: 'undated', items: undated });
  return groups;
}

// --- template fitting -------------------------------------------------------

/** Justified row: pick the height at which these aspects exactly fill the width. */
function justifiedHeight(items: PhotoItem[], width: number, gap: number): number {
  const totalAspect = items.reduce((sum, item) => sum + aspectOf(item), 0);
  const usable = width - gap * (items.length - 1);
  return usable / Math.max(totalAspect, 0.01);
}

interface StackGeometry {
  leftWidth: number;
  rightWidth: number;
  height: number;
  topHeight: number;
  bottomHeight: number;
}

/**
 * Solve the stack so all three photos keep their natural aspect ratios.
 *
 * An earlier version handed the two right-hand photos a fixed box and let `object-cover` crop
 * whatever didn't fit, which is how faces ended up sliced off. Instead, find the column split
 * where the left photo's height exactly equals the two stacked heights plus the gap:
 *
 *   Lw / aA = Rw · (1/aB + 1/aC) + gap,  with  Lw + Rw = width − gap
 *
 * Returns null when the solution is out of proportion or too tall — the caller then falls back
 * to a justified row, which never crops either.
 */
function stackGeometry(items: PhotoItem[], width: number, m: Metrics): StackGeometry | null {
  const [a, b, c] = items;
  if (!a || !b || !c) return null;

  const aA = aspectOf(a);
  const inverseRight = 1 / aspectOf(b) + 1 / aspectOf(c);
  const available = width - m.gap;

  const rightWidth = (available / aA - m.gap) / (inverseRight + 1 / aA);
  if (!Number.isFinite(rightWidth) || rightWidth <= 0) return null;

  const leftWidth = available - rightWidth;
  if (leftWidth <= 0) return null;

  // Guard the proportions: outside this band it stops reading as "one large beside two small".
  const rightFraction = rightWidth / available;
  if (rightFraction < 0.22 || rightFraction > 0.5) return null;

  const height = leftWidth / aA;
  if (height > m.stackCap) return null;

  return {
    leftWidth,
    rightWidth,
    height,
    topHeight: rightWidth / aspectOf(b),
    bottomHeight: rightWidth / aspectOf(c),
  };
}

function buildSpread(
  template: TemplateId,
  items: PhotoItem[],
  startIndex: number,
  width: number,
  m: Metrics,
  key: string
): Spread {
  const photos: PlacedPhoto[] = [];
  let height = 0;

  if (template === 'hero') {
    const item = items[0];
    height = Math.min(width / aspectOf(item), m.heroCap);
    photos.push({ item, index: startIndex, x: 0, y: 0, width, height });
  } else if (template === 'offset-single') {
    const item = items[0];
    let w = width * 0.62;
    height = w / aspectOf(item);
    // Shrink the width rather than cropping — a portrait given a wide box loses its subject.
    if (height > m.singleCap) {
      height = m.singleCap;
      w = height * aspectOf(item);
    }
    // Centre anything that isn't wide enough to carry an offset. Symmetric margins read as
    // deliberate; a small photo pinned to one edge with a void beside it reads as broken.
    const x =
      w < width * OFFSET_SINGLE_FRACTION
        ? (width - w) / 2
        : startIndex % 2 === 1
          ? width - w
          : 0;
    photos.push({ item, index: startIndex, x, y: 0, width: w, height });
  } else if (template === 'row') {
    height = justifiedHeight(items, width, m.gap);
    let rowWidth = width;
    if (height > m.rowCap) {
      height = m.rowCap;
      rowWidth =
        items.reduce((sum, item) => sum + height * aspectOf(item), 0) + m.gap * (items.length - 1);
    }
    // A capped row no longer fills the width, so centre it and let the margins be whitespace.
    let x = (width - rowWidth) / 2;
    items.forEach((item, i) => {
      const w = height * aspectOf(item);
      photos.push({ item, index: startIndex + i, x, y: 0, width: w, height });
      x += w + m.gap;
    });
  } else {
    // stack: one tall on the left, two stacked on the right — every box matches its photo's
    // own aspect ratio, so nothing is cropped.
    const geometry = stackGeometry(items, width, m);
    if (!geometry) {
      // Shouldn't happen: chooseTemplate only picks `stack` when the geometry solves.
      return buildSpread('row', items, startIndex, width, m, key);
    }

    height = geometry.height;
    photos.push({
      item: items[0],
      index: startIndex,
      x: 0,
      y: 0,
      width: geometry.leftWidth,
      height: geometry.height,
    });
    photos.push({
      item: items[1],
      index: startIndex + 1,
      x: geometry.leftWidth + m.gap,
      y: 0,
      width: geometry.rightWidth,
      height: geometry.topHeight,
    });
    photos.push({
      item: items[2],
      index: startIndex + 2,
      x: geometry.leftWidth + m.gap,
      y: geometry.topHeight + m.gap,
      width: geometry.rightWidth,
      height: geometry.bottomHeight,
    });
  }

  return { key, template, photos, height, top: 0 };
}

// --- template choice --------------------------------------------------------

/**
 * How many photos to justify into one row.
 *
 * A fixed count cannot work across container widths, so try each allowed count and keep the one
 * closest to the target height.
 */
function bestRowCount(queue: PhotoItem[], width: number, m: Metrics): number {
  const most = Math.min(m.rowMax, queue.length);
  if (most <= m.rowMin) return Math.min(m.rowMin, queue.length);

  let best = m.rowMin;
  let bestDelta = Infinity;
  for (let n = m.rowMin; n <= most; n++) {
    const delta = Math.abs(justifiedHeight(queue.slice(0, n), width, m.gap) - m.rowTarget);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = n;
    }
  }

  // Never strand a single photo as the chapter's last spread. Taking one fewer leaves it a
  // partner; taking one more absorbs it.
  if (queue.length - best === 1 && m.rowMin > 1) {
    best = best < m.rowMax ? best + 1 : best - 1;
  }
  return best;
}

/** Rendered width of this photo if it were given a spread of its own. */
function singleWidth(item: PhotoItem, width: number, m: Metrics): number {
  const w = width * 0.62;
  const height = w / aspectOf(item);
  return height > m.singleCap ? m.singleCap * aspectOf(item) : w;
}

interface TemplateChoice {
  template: TemplateId;
  count: number;
}

/**
 * Shape leads, interest breaks ties (D-7).
 *
 * Note this picks a *template to suit the upcoming photos*, rather than picking photos to fill
 * a template. That keeps chapters in date order — reordering within a month to chase shapes
 * would make the sequence look arbitrary.
 */
function chooseTemplate(
  queue: PhotoItem[],
  spreadsSinceHero: number,
  width: number,
  m: Metrics
): TemplateChoice {
  const [a] = queue;
  const aspectA = aspectOf(a);
  const remaining = queue.length;

  const wide = aspectA >= 1.45;
  const portrait = aspectA <= 0.85;

  const heroAllowed = spreadsSinceHero >= HERO_MIN_GAP;
  const heroForced = spreadsSinceHero >= HERO_MAX_GAP;

  // Heroes are full-bleed and crop to fit, so only ever give one to a landscape photo. Forcing
  // a portrait into a wide box is exactly how faces get sliced off.
  if (heroAllowed && !portrait && (heroForced || (wide && a.interest >= 0.45))) {
    return { template: 'hero', count: 1 };
  }
  if (remaining === 1) {
    return { template: wide ? 'hero' : m.allowSingle ? 'offset-single' : 'row', count: 1 };
  }

  // Only when the geometry solves exactly — otherwise a row, which never crops either.
  if (m.allowStack && remaining >= 3 && stackGeometry(queue.slice(0, 3), width, m)) {
    return { template: 'stack', count: 3 };
  }

  // A lone striking portrait earns room rather than being squeezed into a row — but only when
  // it is still substantial at this container width.
  if (
    m.allowSingle &&
    portrait &&
    singleWidth(a, width, m) >= width * MIN_SINGLE_FRACTION &&
    a.interest >= 0.55 &&
    spreadsSinceHero >= 1
  ) {
    return { template: 'offset-single', count: 1 };
  }

  return { template: 'row', count: bestRowCount(queue, width, m) };
}

// --- entry point ------------------------------------------------------------

export function buildLayout(
  items: PhotoItem[],
  width: number,
  viewportHeight: number,
  options: LayoutOptions = DEFAULT_LAYOUT_OPTIONS
): Layout {
  if (width <= 0 || items.length === 0) {
    return { chapters: [], totalHeight: 0, ordered: [] };
  }

  const m = metricsFor(width, viewportHeight);
  const narrow = m.rowMax === 1;
  const groups = groupIntoChapters(items, options);
  const ordered: PhotoItem[] = [];
  const chapters: Chapter[] = [];
  let cursor = 0;

  for (const group of groups) {
    const chapterTop = cursor;
    let spreadTop = chapterTop + m.headerHeight;

    const spreads: Spread[] = [];
    const queue = [...group.items];
    let spreadsSinceHero = HERO_MIN_GAP; // let each chapter open with a hero

    while (queue.length > 0) {
      const { template, count } = chooseTemplate(queue, spreadsSinceHero, width, m);
      const slice = queue.splice(0, Math.min(count, queue.length));

      const spread = buildSpread(
        template,
        slice,
        ordered.length,
        width,
        m,
        `${group.key}:${spreads.length}`
      );
      spread.top = spreadTop;
      spreadTop += spread.height + m.gap;
      spreads.push(spread);
      ordered.push(...slice);

      spreadsSinceHero = template === 'hero' ? 0 : spreadsSinceHero + 1;
    }

    const height = spreadTop - chapterTop;
    chapters.push({
      key: group.key,
      label: chapterLabel(group.key, narrow),
      count: group.items.length,
      top: chapterTop,
      headerHeight: m.headerHeight,
      spreads,
      height,
    });
    cursor = chapterTop + height + m.chapterGap;
  }

  return { chapters, totalHeight: Math.max(cursor - m.chapterGap, 0), ordered };
}
